import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Send, Heart, Loader2, Mic, MicOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { callFunction } from '@/lib/ensureSession';
import { useWater } from '@/hooks/useWater';
import { useUserStore } from '@/store/userStore';
import { useDayMeals } from '@/hooks/useMeals';
import { useTodaySteps } from '@/hooks/useSteps';
import { useTelegram } from '@/hooks/useTelegram';

interface Message {
  role: 'user' | 'slava';
  text: string;
}

const SUGGESTIONS = [
  'Как мой день?',
  'Что добавить в питание?',
  'Не худею, что делать?',
  'Можно ли вечером сладкое?',
  'Чувствую усталость',
  'Дай мотивацию',
];

// Web Speech API типы
type SpeechRecognitionInstance = any;

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

export default function TalkToSlava() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const { meals } = useDayMeals();
  const { steps } = useTodaySteps();
  const { haptic } = useTelegram();
  const water = useWater();

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [used, setUsed] = useState(0);
  const [limit, setLimit] = useState(10);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const profileRef = useRef(profile);

  // Обновляем ref при изменении profile
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  // Инициализация Speech Recognition
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setVoiceSupported(false);
      return;
    }

    setVoiceSupported(true);
    const recognition = new SR();
    recognition.lang = 'ru-RU';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onresult = (event: any) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        setInput((prev) => prev + finalTranscript);
      } else if (interimTranscript) {
        setInput(interimTranscript);
      }
    };

    recognition.onerror = (e: any) => {
      console.error('Speech recognition error:', e.error);
      setIsRecording(false);
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognitionRef.current = recognition;

    return () => {
      try {
        recognition.stop();
      } catch {}
    };
  }, []);

  function toggleRecording() {
    if (!recognitionRef.current) return;

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
      haptic('light');
    } else {
      setInput('');
      try {
        recognitionRef.current.start();
        setIsRecording(true);
        haptic('medium');
      } catch (e) {
        console.error(e);
        setIsRecording(false);
      }
    }
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  async function send(text: string) {
    if (!text.trim() || loading || !profile) return;

    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }

    const userMsg: Message = { role: 'user', text };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setLoading(true);
    haptic('light');

    try {
      const res = await callFunction('ai-chat', {
            message: text,
            profile,
            todayMeals: meals,
            todaySteps: steps,
            todayWater: { drunk_ml: water.ml, food_ml: water.foodMl, total_ml: water.total, goal_ml: water.goal },
            userId: profile.id,
          });

      const data = await res.json();
      const reply = data.reply || data.error || 'Что-то связь подвисла, попробуй ещё раз.';

      setMessages((m) => [...m, { role: 'slava', text: reply }]);
      setUsed(data.used ?? 0);
      setLimit(data.limit ?? 10);
      setIsAdmin(!!data.is_admin);
      haptic('success');
    } catch (e) {
      console.error(e);
      setMessages((m) => [...m, { role: 'slava', text: '⚠️ Не получается ответить. Попробуй через минуту.' }]);
      haptic('error');
    } finally {
      setLoading(false);
    }
  }

  const limitReached = !isAdmin && used >= limit;

  return (
    <div className="flex flex-col h-[100dvh] max-w-md mx-auto">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex items-center gap-3 border-b border-line/50">
        <button
          onClick={() => nav(-1)}
          className="w-9 h-9 rounded-xl bg-bg-card border border-line flex items-center justify-center"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-2xl">💬</span>
            <div>
              <div className="font-display font-bold text-base leading-tight">Поговорить с Дофи</div>
              <div className="text-[10px] text-ink-dim">
                {isAdmin ? '∞ Безлимит' : `${used}/${limit} сегодня`}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="text-5xl mb-3">🤝</div>
            <div className="font-display font-bold text-lg mb-2">
              Привет, {profile?.first_name || 'друг'}!
            </div>
            <p className="text-sm text-ink-muted leading-relaxed mb-6">
              Спроси о чём угодно — про еду, тренировки, мотивацию, самочувствие.
              Я отвечу честно, без занудства.
            </p>

            <div className="text-[11px] text-ink-dim uppercase tracking-wider mb-3">
              Идеи для разговора
            </div>
            <div className="grid grid-cols-2 gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  disabled={limitReached}
                  className="card p-3 text-xs text-left hover:bg-bg-elevated disabled:opacity-40 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className={`mb-3 flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-emerald-glow text-bg font-medium'
                  : 'bg-bg-card border border-line'
              }`}
            >
              {m.text}
            </div>
          </motion.div>
        ))}

        {loading && (
          <div className="flex justify-start mb-3">
            <div className="bg-bg-card border border-line rounded-2xl px-4 py-3 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin text-emerald-glow" />
              <span className="text-xs text-ink-muted">Дофи думает…</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-5 pb-5 pt-2 border-t border-line/50">
        {limitReached ? (
          <div className="text-center py-3">
            <Heart size={24} className="text-warn mx-auto mb-2" />
            <div className="text-sm text-ink-muted">
              Сегодня лимит исчерпан ({limit} вопросов). Завтра поговорим снова 🌙
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="flex gap-2 items-end"
          >
            <div className="flex-1 relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={isRecording ? 'Говори…' : 'Спроси Дофи о чём угодно…'}
                disabled={loading}
                className={`input w-full text-sm pr-10 ${isRecording ? 'border-emerald-glow animate-pulse' : ''}`}
                autoFocus
              />
              {voiceSupported && (
                <button
                  type="button"
                  onClick={toggleRecording}
                  disabled={loading}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                    isRecording
                      ? 'bg-emerald-glow/20 text-emerald-glow'
                      : 'text-ink-dim hover:text-ink hover:bg-bg-elevated'
                  }`}
                  title={isRecording ? 'Стоп' : 'Голосовой ввод'}
                >
                  {isRecording ? <MicOff size={14} /> : <Mic size={14} />}
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="w-11 h-11 rounded-xl bg-emerald-glow text-bg flex items-center justify-center disabled:opacity-40 shrink-0"
            >
              <Send size={16} />
            </button>
          </form>
        )}

        {!voiceSupported && messages.length === 0 && (
          <p className="text-[10px] text-ink-dim text-center mt-2">
            💡 Голосовой ввод не поддерживается в этом браузере
          </p>
        )}

        {isRecording && (
          <div className="flex items-center justify-center gap-2 mt-2 text-xs text-emerald-glow">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-glow animate-pulse" />
            Запись… Нажми микрофон чтобы остановить
          </div>
        )}
      </div>
    </div>
  );
}
